import React, { useState, useEffect } from "react";
import { API, graphqlOperation, Storage, Auth } from "aws-amplify";
import TextField from "@mui/material/TextField";
import {
  FormControl,
  InputAdornment,
  IconButton,
  Box,
  Typography,
  Snackbar,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SearchPartRow from "./SearchPartRow";
import PartDetails from "../parts/PartDetails";
import { getPricelist, listPricelists, getUser } from "../../graphql/queries";
import {
  updateInvoice,
  createPart,
  createSearch,
  updateUser,
} from "../../graphql/mutations";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useUser } from "../../UserContext";
import {
  getUserPricingConfig,
  processPricelistWithGroupAdjustments,
  applyStackedAdjustments,
} from "../../utils/pricingUtils";

const SearchPricelistForm = ({
  defaultSearch = "",
  onPartSelect,
  showPricelistSelect = true,
  showDisclaimers = true,
  csvData,
  setCsvData,
  selectedPricelist,
  fetchLatestCSV,
  userClass,
  onLoadPricelistData,
}) => {
  const [searchTerm, setSearchTerm] = useState(defaultSearch || "");
  const {
    userClass: userClassFromContext,
    userRecord,
    refreshUserAndPricelist,
  } = useUser();

  const [filteredCsvData, setFilteredCsvData] = useState([]);
  const [searchedPart, setSearchedPart] = useState(null);
  const [user, setUser] = useState(null);
  const [loadingUI, setLoadingUI] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [doesHaveOpenInvoices, setDoesHaveOpenInvoices] = useState(false);
  const [recentPricelists, setRecentPricelists] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userGroupAdjustments, setUserGroupAdjustments] = useState([]);
  const [pricingType, setPricingType] = useState("none"); // 'group', 'individual', or 'none'
  const [lastCSV, setLastCSV] = useState({});
  const navigate = useNavigate();
  const theme = useTheme();

  // Fetch user pricing configuration (group or individual adjustments)
  useEffect(() => {
    const fetchUserPricingConfig = async () => {
      if (userRecord && userRecord.id) {
        try {
          const pricingConfig = await getUserPricingConfig(userRecord);
          setUserGroupAdjustments(pricingConfig.adjustments);
          setPricingType(pricingConfig.type);
          console.log("User pricing config loaded:", pricingConfig);
        } catch (error) {
          console.error("Error loading user pricing config:", error);
        }
      }
    };

    fetchUserPricingConfig();
  }, [userRecord]);

  // Debug log when component renders
  useEffect(() => {
    console.log(
      `SearchPricelistForm rendered with csvData length: ${csvData?.length || 0}`,
    );
    console.log("UserRecord:", userRecord);
    console.log("Active pricelist ID:", userRecord?.activePricelistID);
    console.log("Active pricelist object:", userRecord?.activePricelist);
    console.log("Has activePricelistID?", !!userRecord?.activePricelistID);
    console.log("User ID:", userRecord?.id);
    console.log("User email:", userRecord?.email);
  }, [csvData, userRecord]);

  // Force refresh user record if activePricelistID is missing but user exists
  useEffect(() => {
    const checkAndRefreshUser = async () => {
      if (userRecord && userRecord.id && !userRecord.activePricelistID) {
        console.log(
          "[SearchPricelist] User has no activePricelistID, checking database directly...",
        );
        try {
          // Query the database directly to see what's actually stored
          const directUserResponse = await API.graphql(
            graphqlOperation(getUser, { id: userRecord.id }),
          );
          const directUser = directUserResponse.data.getUser;
          console.log("[SearchPricelist] Direct database query result:", {
            id: directUser.id,
            email: directUser.email,
            activePricelistID: directUser.activePricelistID,
            hasActivePricelist: !!directUser.activePricelist,
          });

          // If the database has a pricelist but userRecord doesn't, refresh
          if (directUser.activePricelistID && !userRecord.activePricelistID) {
            console.log(
              "[SearchPricelist] Database has pricelist but userRecord does not, refreshing...",
            );
            const currentUser = await Auth.currentAuthenticatedUser();
            await refreshUserAndPricelist(currentUser);
          }
        } catch (error) {
          console.error(
            "[SearchPricelist] Error checking/refreshing user:",
            error,
          );
        }
      }
    };

    // Only check after a short delay to avoid race conditions
    const timer = setTimeout(checkAndRefreshUser, 2000);
    return () => clearTimeout(timer);
  }, [userRecord?.id, userRecord?.activePricelistID, refreshUserAndPricelist]);

  // Fetch recent pricelists if user is Admin or SuperAdmin
  const fetchRecentPricelists = async () => {
    if (!userRecord || !userRecord.id) {
      console.log("User record not available yet");
      return;
    }

    try {
      // For Admin/SuperAdmin, fetch all pricelists where they are the owner (userId)
      // This ensures they can see all pricelists they uploaded
      const response = await API.graphql(
        graphqlOperation(listPricelists, {
          limit: 1000,
          filter: {
            userId: { eq: userRecord.id },
          },
        }),
      );

      const pricelists = response.data.listPricelists.items || [];

      // Sort by createdAt date (most recent first)
      const sortedPricelists = pricelists.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );

      setRecentPricelists(sortedPricelists);
      console.log(
        `Fetched ${sortedPricelists.length} pricelists for Admin/SuperAdmin`,
      );
    } catch (error) {
      console.error("Error fetching recent pricelists:", error);
      setRecentPricelists([]);
    }
  };

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((usr) => {
        setUser(usr);
        if (
          userClassFromContext &&
          ["Admin", "SuperAdmin"].includes(userClassFromContext) &&
          userRecord?.id
        ) {
          fetchRecentPricelists();
        }
      })
      .catch((err) => console.error(err));
  }, [userClassFromContext, userRecord]);

  // Get user location (used for search logging)
  const getUserLocation = async () => {
    try {
      const response = await fetch("https://api.ipify.org/?format=json");
      const data = await response.json();
      const ipAddress = data.ip;

      const locationResponse = await fetch(
        `https://ipapi.co/${ipAddress}/json/`,
      );
      const locationData = await locationResponse.json();
      return {
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        ipAddress,
      };
    } catch (error) {
      console.error("Error getting user location:", error);
      return { latitude: null, longitude: null, ipAddress: null };
    }
  };

  const handleResetSearch = () => {
    setSearchedPart(null);
    setSearchTerm("");
    setFilteredCsvData([]);
  };

  // Search for a part in the CSV data and log the search
  const handleSearch = async (part) => {
    console.log("handleSearch called", part);
    if (!csvData || !Array.isArray(csvData)) {
      console.error("csvData is not available or not an array");
      return;
    }

    const results = csvData.filter(
      (row) =>
        row["PART NUMBER"] &&
        row["PART NUMBER"].toString().replace(/\s+/g, "").toLowerCase() ===
          part["PART NUMBER"].toString().replace(/\s+/g, "").toLowerCase(),
    );
    console.log("results", results);
    const result = results[0];

    // Apply all stacked adjustments (pricelist + user/group + provider)
    const basePrice = parseFloat(result?.["PRICE"] || part["PRICE"] || 0);
    const pricelistAdjustments = lastCSV.adjustments
      ? JSON.parse(lastCSV.adjustments)
      : {};

    // Combine user's individual offset with group adjustments if needed
    let allAdjustments = [...userGroupAdjustments];

    // If user has an individual offset and it's not already in the group adjustments,
    // add it to ensure it's applied
    if (
      userRecord &&
      userRecord.priceOffsetType &&
      userRecord.priceOffsetValue !== null &&
      userRecord.priceOffsetValue !== undefined
    ) {
      const hasIndividualAdjustment = allAdjustments.some(
        (adj) => adj.id === "individual",
      );
      if (!hasIndividualAdjustment) {
        allAdjustments.push({
          id: "individual",
          title: "Individual Pricing",
          percentageAdjustment:
            userRecord.priceOffsetType === "percentage"
              ? userRecord.priceOffsetValue
              : 0,
          dollarAdjustment:
            userRecord.priceOffsetType === "fixed"
              ? userRecord.priceOffsetValue
              : 0,
          adjustmentType: userRecord.priceOffsetType,
        });
      }
    }

    // Apply all stacked adjustments
    const finalPrice = applyStackedAdjustments(
      basePrice,
      pricelistAdjustments,
      allAdjustments,
    );

    // Create enhanced part object with group pricing
    const enhancedPart = {
      ...part,
      originalPrice: basePrice,
      finalPrice: finalPrice,
      groupAdjustedPrice: finalPrice,
      appliedGroupAdjustments: allAdjustments,
    };

    setSearchedPart(enhancedPart);
    setSearchTerm("");
    setFilteredCsvData([]);

    if (!userRecord) {
      console.log("User record not available, skipping search logging");
      return;
    }

    try {
      const userDeets = await getUserLocation();
      const search = {
        ipAddress: userDeets.ipAddress,
        latitude: userDeets.latitude,
        longitude: userDeets.longitude,
        price: finalPrice, // Use the price with group adjustments
        partNumber: result?.["PART NUMBER"] || part["PART NUMBER"],
        userSearchedID: userRecord.id,
        pricelistID: userRecord.activePricelistID,
      };

      await API.graphql(graphqlOperation(createSearch, { input: search }));
      console.log("Search record created with group-adjusted price");
    } catch (error) {
      console.error("Error creating search:", error);
    }
  };

  // Filter a raw pricelist by OEM (for search suggestions)
  const fetchPricesByOEMs = (oem) => {
    const oemToSearch = oem.replace("-0", "");
    console.log("csvData length:", csvData?.length || 0);

    // Add better null check and user feedback
    if (!csvData || !Array.isArray(csvData) || csvData.length === 0) {
      console.log("No CSV data available or not in expected format");
      return [];
    }

    const results = csvData
      .filter(
        (row) =>
          row["PART NUMBER"] &&
          row["PART NUMBER"]
            .toString()
            .toLowerCase()
            .includes(oemToSearch.toLowerCase()),
      )
      .slice(0, 20);

    console.log(
      `Found ${results.length} results for search term: ${oemToSearch}`,
    );
    return results;
  };

  useEffect(() => {
    // Simple authentication check
    Auth.currentAuthenticatedUser()
      .then((usr) => setUser(usr))
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    if (searchTerm.length >= 2 && csvData) {
      const results = fetchPricesByOEMs(searchTerm);
      console.log("results in useEffect", results);
      setFilteredCsvData(results);
    } else {
      setFilteredCsvData([]);
    }
  }, [searchTerm, csvData]);

  const handlePartClick = (part, grade) => {
    if (onPartSelect) {
      onPartSelect(part, grade);
    } else {
      handleSearch(part);
    }
  };

  // When an admin changes the pricelist, update the user and then refresh CSV data.
  const handlePricelistChange = async (event) => {
    const selectedId = event.target.value;
    const selected = recentPricelists.find((pl) => pl.id === selectedId);

    if (selected) {
      try {
        setLoadingUI(true);
        setLastCSV(selected); // Update lastCSV when pricelist changes
        await API.graphql(
          graphqlOperation(updateUser, {
            input: {
              id: userRecord.id,
              activePricelistID: selected.id,
            },
          }),
        );
        const currentUser = await Auth.currentAuthenticatedUser();
        await refreshUserAndPricelist(currentUser);

        // Clear existing localStorage data
        localStorage.removeItem("csvData");
        localStorage.removeItem("uploadedAt");

        // Fetch fresh data from S3
        await onLoadPricelistData(selected.id);
        setShowFeedback(true);
      } catch (error) {
        console.error("Error updating active pricelist:", error);
      } finally {
        setLoadingUI(false);
      }
    }
  };

  if (!userRecord) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div style={{ margin: "0", padding: "0" }}>
      <>
        {loading || loadingUI ? (
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: "bold",
              marginBottom: "20px",
            }}
          >
            Loading pricelist...
          </Typography>
        ) : userRecord?.activePricelistID ? (
          <>
            <hr
              style={{
                color: theme.palette.primary.main,
                marginTop: "20px",
                marginBottom: "20px",
              }}
            />
            <Box
              sx={{
                padding: 2,
                backgroundColor: theme.palette.background.paper,
                borderRadius: "0",
                marginTop: "0px",
              }}
            >
              {/* Show pricelist information */}
              {userRecord?.activePricelistID && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: theme.palette.primary.main }}
                  >
                    Active Pricelist:{" "}
                    {userRecord.activePricelist?.name ||
                      lastCSV?.name ||
                      `Pricelist #${userRecord.activePricelistID}`}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    CSV Data Status:{" "}
                    {csvData && csvData.length > 0
                      ? `${csvData.length} records loaded`
                      : "No data loaded - please refresh or select a different pricelist"}
                  </Typography>
                  {userGroupAdjustments.length > 0 &&
                    userClassFromContext &&
                    ["Admin", "SuperAdmin"].includes(userClassFromContext) && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ color: "success.main", mt: 1 }}
                      >
                        ✓{" "}
                        {pricingType === "group"
                          ? `Group pricing applied from: ${userGroupAdjustments.map((adj) => adj.title).join(", ")}`
                          : `Individual pricing adjustments applied`}
                      </Typography>
                    )}
                </Box>
              )}

              {showPricelistSelect &&
                userClassFromContext &&
                ["Admin", "SuperAdmin"].includes(userClassFromContext) && (
                  <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                    <InputLabel id="pricelist-select-label">
                      Select Pricelist
                    </InputLabel>
                    <Select
                      labelId="pricelist-select-label"
                      id="pricelist-select"
                      value={userRecord.activePricelistID || ""}
                      label="Select Pricelist"
                      onChange={handlePricelistChange}
                      sx={{
                        color: theme.palette.text.primary,
                        ".MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.primary.main,
                        },
                        ".MuiSvgIcon-root": {
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      {recentPricelists?.map((pricelist) => (
                        <MenuItem key={pricelist.id} value={pricelist.id}>
                          {pricelist.name ||
                            pricelist.filename ||
                            `Pricelist #${pricelist.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              <FormControl sx={{ width: "100%" }} variant="outlined">
                <TextField
                  id="filled-basic"
                  label="Search"
                  placeholder="e.g. EC4R 3TE"
                  value={searchTerm}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    marginBottom: "10px",
                    marginTop: "20px",
                    borderRadius: "20px",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: theme.palette.primary.main,
                        borderRadius: "20px",
                      },
                      "&:hover fieldset": {
                        borderColor: theme.palette.primary.main,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: theme.palette.text.secondary,
                    },
                    "& .MuiInputBase-input": {
                      color: theme.palette.text.primary,
                    },
                    input: {
                      color: theme.palette.text.primary,
                      "&::placeholder": {
                        color: theme.palette.text.secondary,
                        opacity: 0.7,
                      },
                    },
                  }}
                  onChange={(evt) => setSearchTerm(evt.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          sx={{
                            color: theme.palette.background.paper,
                            backgroundColor: theme.palette.secondary.main,
                            borderRadius: "50%",
                            padding: "8px",
                            "&:hover": {
                              backgroundColor: theme.palette.secondary.dark,
                            },
                          }}
                          edge="end"
                        >
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </Box>
            <Box sx={{ marginTop: "20px" }}>
              {filteredCsvData.length > 0 && (
                <Box
                  sx={{
                    maxWidth: "600px",
                    margin: "0 auto",
                    backgroundColor: "#000000",
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: "8px",
                    padding: "12px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: "4px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      marginBottom: "8px",
                      fontSize: "0.8rem",
                      textAlign: "center",
                    }}
                  >
                    {filteredCsvData.length} result
                    {filteredCsvData.length !== 1 ? "s" : ""} found
                    {userGroupAdjustments.length > 0 && (
                      <span style={{ color: theme.palette.success.main }}>
                        {" "}
                        • {pricingType === "group"
                          ? "Group"
                          : "Individual"}{" "}
                        pricing applied
                      </span>
                    )}
                  </Typography>
                  {filteredCsvData.map((u, i) => {
                    // Calculate final price with all adjustments (pricelist + user/group + provider)
                    const basePrice = parseFloat(
                      u["PRICE"] || u.adjustedPrice || 0,
                    );
                    const pricelistAdjustments = lastCSV.adjustments
                      ? JSON.parse(lastCSV.adjustments)
                      : {};

                    // Combine user's individual offset with group adjustments if needed
                    let allAdjustments = [...userGroupAdjustments];

                    // If user has an individual offset and it's not already in the group adjustments,
                    // add it to ensure it's applied
                    if (
                      userRecord &&
                      userRecord.priceOffsetType &&
                      userRecord.priceOffsetValue !== null &&
                      userRecord.priceOffsetValue !== undefined
                    ) {
                      const hasIndividualAdjustment = allAdjustments.some(
                        (adj) => adj.id === "individual",
                      );
                      if (!hasIndividualAdjustment) {
                        allAdjustments.push({
                          id: "individual",
                          title: "Individual Pricing",
                          percentageAdjustment:
                            userRecord.priceOffsetType === "percentage"
                              ? userRecord.priceOffsetValue
                              : 0,
                          dollarAdjustment:
                            userRecord.priceOffsetType === "fixed"
                              ? userRecord.priceOffsetValue
                              : 0,
                          adjustmentType: userRecord.priceOffsetType,
                        });
                      }
                    }

                    // Apply all stacked adjustments
                    const finalPrice = applyStackedAdjustments(
                      basePrice,
                      pricelistAdjustments,
                      allAdjustments,
                    );

                    // Create enhanced part with all pricing information
                    const enhancedPart = {
                      ...u,
                      originalPrice: basePrice,
                      finalPrice: finalPrice,
                      groupAdjustedPrice: finalPrice, // Use finalPrice for backward compatibility
                      appliedGroupAdjustments: allAdjustments,
                    };

                    return (
                      <SearchPartRow
                        part={enhancedPart}
                        key={i}
                        onClick={handlePartClick}
                        isInvoiceMode={!!onPartSelect}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          </>
        ) : (
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: "bold",
              marginBottom: "20px",
            }}
          >
            No pricelists have been uploaded.
          </Typography>
        )}

        {searchedPart && !onPartSelect && (
          <PartDetails
            part={searchedPart}
            onResetSearch={handleResetSearch}
            userGroupAdjustments={userGroupAdjustments}
            lastCSV={lastCSV}
          />
        )}

        <Snackbar
          open={showFeedback}
          autoHideDuration={3000}
          onClose={() => setShowFeedback(false)}
          message={`Successfully updated pricelist.`}
          ContentProps={{
            sx: {
              background: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          }}
        />
      </>
    </div>
  );
};

// Main component wrapper that handles data loading and error catching
const SearchPricelist = (props) => {
  const [csvData, setCsvData] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { userRecord } = useUser();
  const theme = useTheme();

  // Add function to load CSV data from S3 using the pricelist ID
  const loadPricelistData = async (pricelistId) => {
    if (!pricelistId) return;

    try {
      setLoading(true);
      console.log(`Loading pricelist data for ID: ${pricelistId}`);

      // First get the pricelist details to get the S3 key
      const response = await API.graphql(
        graphqlOperation(getPricelist, { id: pricelistId }),
      );

      const pricelist = response.data.getPricelist;
      if (!pricelist || !pricelist.s3Key) {
        console.error("No S3 key found for pricelist");
        setLoading(false);
        return;
      }

      // Fetch data from S3
      const fileUrl = await Storage.get(pricelist.s3Key, {
        download: true,
      });

      if (fileUrl.Body) {
        const blob = new Blob([fileUrl.Body], { type: "application/json" });
        const jsonText = await blob.text();
        const jsonData = JSON.parse(jsonText);

        if (jsonData && jsonData.length > 0) {
          // Update localStorage with new data
          localStorage.setItem("csvData", JSON.stringify(jsonData));
          localStorage.setItem("uploadedAt", pricelist.createdAt);
          localStorage.setItem("currentPricelistId", pricelistId);
          setCsvData(jsonData);
          console.log(`Loaded ${jsonData.length} items from S3`);
        } else {
          console.error("No data or empty data received from S3");
        }
      } else {
        console.error("No file body received from S3");
      }
    } catch (err) {
      console.error("Error loading pricelist data from S3:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadCsvData = async () => {
      try {
        setLoading(true);

        // Check if we have a current pricelistId from props
        const targetPricelistId =
          props.pricelistId || userRecord?.activePricelistID;

        // Try to get data from localStorage first
        const cachedData = localStorage.getItem("csvData");
        const storedPricelistId = localStorage.getItem("currentPricelistId");

        if (cachedData && storedPricelistId === targetPricelistId) {
          console.log(`Using cached data for pricelist ${targetPricelistId}`);
          setCsvData(JSON.parse(cachedData));
        } else if (targetPricelistId) {
          // If no cached data or different pricelist, fetch from S3
          console.log(
            `No cached data found for ${targetPricelistId}, fetching from S3`,
          );
          await loadPricelistData(targetPricelistId);
        } else {
          console.log("No pricelist ID available");
        }

        setDataLoaded(true);
      } catch (err) {
        console.error("Error loading CSV data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
        setDataLoaded(true);
      }
    };

    loadCsvData();
  }, [props.pricelistId, userRecord?.activePricelistID]);

  useEffect(() => {
    if (csvData && csvData.length > 0) {
      console.log(`CSV data loaded with ${csvData.length} items`);
    }
  }, [csvData]);

  if (loading && !dataLoaded) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
        <Typography sx={{ ml: 2, color: theme.palette.text.primary }}>
          Loading pricelist data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error" variant="h6">
          Error loading pricelist data: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <SearchPricelistForm
      {...props}
      csvData={csvData}
      setCsvData={setCsvData}
      onLoadPricelistData={loadPricelistData}
    />
  );
};

export default SearchPricelist;
